import {resolveAllOverrides} from "../data/default-scene-overrides";
import {resolveLegacySceneId, resolveOverrideSceneData, preferLegacyDefaults} from "../data/default-scenes";
import {mergeScenes} from "../data/merge-scenes";
import {resolveBannerSelection, resolveOfferedBannerSelection, type BannerSelection} from "../data/template-availability";
import {bannerFromLegacyScene, type LegacyScene} from "./legacy";
import {baseColorSchema, type BannerPatch} from "./document";
import * as s from "./schema";
import {tileBaseColor} from "../libs/tileBase";

export type DefaultOverride = {
  channel: string; product: string; type: string;
  set?: string | null; layout?: string | null; background?: string | null;
  scene_data?: LegacyScene | null; scene_hash?: string | null;
};
export type CreateBannerInput = BannerSelection & {
  instanceId: string; layout: number; background: string; patch?: BannerPatch;
};
export type DefaultLayers = {
  overrides: DefaultOverride[];
  // Resolved resources are explicit inputs, allowing the same pure creation in UI and Bun.
  scenes: Record<string, LegacyScene>;
  baseScene: LegacyScene;
  backgroundScene: LegacyScene;
};

function defaultPlan(input: CreateBannerInput, overrides: DefaultOverride[]) {
  const selection = resolveOfferedBannerSelection(input);
  if (!selection.layouts.includes(input.layout)) throw new Error(`Unavailable layout: ${input.templateKey}/${input.layout}`);
  const params = {
    channel: selection.definition.channel, productCode: selection.product.code,
    contentType: input.contentType, set: input.set, layout: input.layout, background: input.background,
  };
  const legacySceneId = resolveLegacySceneId({
    typeConfig: selection.type,
    productCode: selection.product.code, set: input.set, layout: input.layout, background: input.background,
  });
  const rows = resolveAllOverrides(overrides, params.channel, params.productCode, params.contentType, params.set, String(params.layout), params.background);
  const scene = resolveOverrideSceneData(overrides, params);
  const useOverride = scene !== null && !preferLegacyDefaults(params.productCode,
    rows.some((row) => row.product === params.productCode), legacySceneId);
  return {scene: useOverride ? scene : null, sceneId: useOverride ? null : legacySceneId};
}

export function resolveBannerDefaults(input: CreateBannerInput, layers: DefaultLayers) {
  const fields = s.object(input, "createBanner");
  for (const key of Object.keys(fields)) {
    if (!["instanceId", "templateKey", "product", "contentType", "set", "layout", "background", "patch"].includes(key)) {
      s.fail(`createBanner.${key}`, "Unknown field");
    }
  }
  s.text(input.background, "createBanner.background");
  s.object(layers.baseScene, "defaults.baseScene");
  s.object(layers.backgroundScene, "defaults.backgroundScene");
  s.object(layers.scenes, "defaults.scenes");
  if (!Array.isArray(layers.overrides)) s.fail("defaults.overrides", "Expected array");
  const plan = defaultPlan(input, layers.overrides);
  let defaults = plan.scene;
  if (plan.sceneId !== null) {
    if (!Object.hasOwn(layers.scenes, plan.sceneId)) throw new Error(`Unresolved default scene: ${plan.sceneId}`);
    defaults = s.object(layers.scenes[plan.sceneId], `scenes.${plan.sceneId}`);
  }
  const scene = mergeScenes([layers.baseScene, layers.backgroundScene, defaults, {layoutOption: input.layout, bgOption: input.background}]);
  if (Object.hasOwn(scene, "base")) baseColorSchema(scene.base, "defaults.base");
  const catalogBase = Object.hasOwn(layers.backgroundScene, "base") ? baseColorSchema(layers.backgroundScene.base, "defaults.catalogBase") : null;
  scene.base = tileBaseColor({scene, catalogBase});
  // Legacy saves an empty customBg for preset mode; the selected gallery image still applies.
  if ((scene.customBg === "" || scene.customBg === null) && layers.backgroundScene.customBg !== undefined) {
    scene.customBg = layers.backgroundScene.customBg;
  }
  return bannerFromLegacyScene(input, scene, input.patch);
}

// API/resource layer supplies the fetchers; the domain has no fetch, React or environment dependencies.
export type DefaultResources = {
  getOverrides: (channel: string) => Promise<DefaultOverride[]>;
  getScene: (sceneId: string) => Promise<LegacyScene>;
  getBaseScene: (input: CreateBannerInput) => Promise<LegacyScene>;
  getBackgroundScene: (input: CreateBannerInput) => Promise<LegacyScene>;
};
export async function createBanner(input: CreateBannerInput, resources: DefaultResources) {
  input = structuredClone(input);
  const {definition} = resolveBannerSelection(input);
  const overrides = await resources.getOverrides(definition.channel);
  const plan = defaultPlan(input, overrides);
  const [baseScene, backgroundScene, defaultScene] = await Promise.all([
    resources.getBaseScene(input), resources.getBackgroundScene(input),
    plan.sceneId === null ? Promise.resolve(null) : resources.getScene(plan.sceneId),
  ]);
  const scenes: Record<string, LegacyScene> = {};
  if (plan.sceneId !== null) scenes[plan.sceneId] = s.object(defaultScene, `scenes.${plan.sceneId}`);
  return resolveBannerDefaults(input, {overrides, scenes, baseScene, backgroundScene});
}
